import React, { Fragment, useMemo, useState } from 'react';
import { get, getOr, map, mapValues, size, split, times } from 'lodash/fp';
import { Button, ButtonToolbar, Icon, Panel } from 'rsuite';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import qs from 'qs';
import { GuestType } from '../../../../interfaces/guest';
import MainSettingsType from '../../../../interfaces/MainSettingsType';
import applyPortalProfileToGuest from '../../../../helpers/applyPortalProfileToGuest';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import guestMailingAddressLine from '../../../../helpers/guestMailingAddressLine';
import guestAddressLine from '../../../../helpers/guestAddressLine';
import { modifyAccess, viewAccess } from '../../../../helpers/permission';
import history from '../../../history';
import { guestsViewPath } from '../../../Paths';
import {
  getAgeFields,
  getHouseholdFields,
  getIncomeAndExpenseFields,
  getStaticCustomFields
} from '../../../../redux/selectors/client';
import {
  getIsAgeBreakdown,
  getIsHouseholdDetails,
  getIsEnabledAgeBreakdown
} from '../../../../redux/Settings/selectors/main';
import {
  MODULE as GUEST_MODULE,
  VIEW_ADDRESS
} from '../../../../constants/permissions/guest';
import {
  DetailsItem,
  ItemLabel,
  ItemValue,
  ContactNumber,
  AgeBreakdown
} from '../../../../components/guest/ViewElements';
import GuestForm from '../../../../components/guest/Form';
import RedLabel from '../../../../components/RedLabel';
import AccessControl from '../../../../components/AccessControl';
import CompactMessage from '../../../../components/CompactMessage';

const HouseholdSection = ({ guest, settings, ...props }) => {
  const { id } = useParams();
  const { search } = useLocation();
  const query = qs.parse(search, { ignoreQueryPrefix: true });
  const applyPortalUpdates = query.fromPortal === '1';
  const [editMode, setEditMode] = useState(
    query.edit === '1' || applyPortalUpdates
  );
  const formData = useMemo(
    () => (applyPortalUpdates ? applyPortalProfileToGuest(guest) : guest),
    [applyPortalUpdates, guest]
  );
  const isAgeBreakdown = useSelector(getIsAgeBreakdown);
  const isEnabledAgeBreakdown = useSelector(getIsEnabledAgeBreakdown);
  const isHouseholdDetails = useSelector(getIsHouseholdDetails);
  // Method 1 (Household Details) or Method 2 (Age Breakdown): show age group when enabled
  const showAgeBreakdown =
    isEnabledAgeBreakdown && (isAgeBreakdown || isHouseholdDetails);
  // Method 2 only: age fields are editable; Method 1 keeps them read-only
  const includeEditableAgeFields = isAgeBreakdown && isEnabledAgeBreakdown;
  const householdFields = useSelector(state =>
    getHouseholdFields(state, includeEditableAgeFields)
  );
  const staticCustomFields = useSelector(getStaticCustomFields);
  const incomeExpenseFields = useSelector(getIncomeAndExpenseFields);
  const ageFields = useSelector(getAgeFields);
  const householdTotal = getOr(1, 'household_total', guest);
  const membersCount = size(get('othersHousehold', guest)) || 0;

  const formAgeFields = showAgeBreakdown
    ? mapValues(
        field => ({
          ...field,
          // Method 1: visible for reference but non-editable
          disabled: isHouseholdDetails
        }),
        ageFields
      )
    : {};

  const formFields = {
    ...householdFields,
    ...formAgeFields,
    ...staticCustomFields,
    ...incomeExpenseFields
  };

  const homePhone = get('phone', guest);
  const workPhone = get('workphone', guest);
  const cellPhone = get('cellphone', guest);
  const isHomeless = get('homeless', guest);
  const { line1, line2 } = guestAddressLine(guest, true, false);
  const {
    line1: mailingAddressLine1,
    line2: mailingAddressLine2
  } = guestMailingAddressLine(guest, true);
  const householdTotalLabel = get('household_total.label', formFields);
  const otherMembersLabel = getOr(
    'Other Members',
    'othersHousehold.label',
    formFields
  );
  const handleEdit = () => {
    setEditMode(true);
  };

  const clearPortalQuery = () => {
    history.replace(
      pagePathToUrl(guestsViewPath, { id, section: 'household' })
    );
  };

  const handleCancel = () => {
    if (applyPortalUpdates) clearPortalQuery();
    setEditMode(false);
  };

  const handleSaved = () => {
    if (applyPortalUpdates) clearPortalQuery();
    setEditMode(false);
  };

  return (
    <Panel {...props}>
      <ButtonToolbar style={{ textAlign: 'right', marginBottom: 16 }}>
        {!editMode && (
          <AccessControl allowOnlyFor={modifyAccess(GUEST_MODULE)}>
            <Button appearance="primary" onClick={handleEdit}>
              <Icon icon="pencil" /> Edit
            </Button>
          </AccessControl>
        )}
      </ButtonToolbar>

      {isHouseholdDetails &&
        membersCount > householdTotal - 1 && (
          <CompactMessage
            showIcon
            type="info"
            description={`${householdTotalLabel} does not match the total number of Household Members. Update "${householdTotalLabel}" value to ${membersCount +
              1} or "${otherMembersLabel}" to match it.`}
          />
        )}

      {editMode ? (
        <Fragment>
          {applyPortalUpdates && (
            <CompactMessage
              showIcon
              type="info"
              description="Form is pre-filled with the guest's latest Portal address and phone numbers. Review and save to update this record."
            />
          )}
          <GuestForm
            data={formData}
            section="household"
            formFields={formFields}
            onSaved={handleSaved}
            onCancel={handleCancel}
          />
        </Fragment>
      ) : (
        <Fragment>
          <DetailsItem>
            <ItemLabel>{get('blackball.label', formFields)}</ItemLabel>
            <ItemValue>{get('blackball', guest) ? 'Yes' : 'No'}</ItemValue>
          </DetailsItem>

          {settings.guestAdditionalMember === '1' && (
            <DetailsItem>
              <ItemLabel>{get('isMember.label', formFields)}</ItemLabel>
              <ItemValue>{get('isMember', guest) ? 'Yes' : 'No'}</ItemValue>
            </DetailsItem>
          )}

          {settings.barcode === '1' && (
            <DetailsItem>
              <ItemLabel>{get('barcode.label', formFields)}</ItemLabel>
              <ItemValue>{get('barcode', guest) || 'Not Assigned'}</ItemValue>
            </DetailsItem>
          )}

          <DetailsItem>
            <ItemLabel>{get('household_total.label', formFields)}</ItemLabel>
            <ItemValue>{get('household_total', guest)}</ItemValue>
          </DetailsItem>

          {showAgeBreakdown && (
            <DetailsItem>
              <ItemLabel>Household Age Group</ItemLabel>
              <ItemValue>
                <AgeBreakdown style={{ color: '#999' }}>
                  <span>
                    <small>Age Group</small>
                  </span>
                  <span>
                    <small># of Individual</small>
                  </span>
                </AgeBreakdown>
                {times(
                  i => (
                    <AgeBreakdown key={`age-${i}`}>
                      <span>
                        {getOr('', `age${i + 1}.label`, formFields).replace(
                          'Age: ',
                          ''
                        )}
                      </span>
                      <span>{get(`age${i + 1}`, guest)}</span>
                    </AgeBreakdown>
                  ),
                  settings.ageGroupCount
                )}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalLocation === '1' && (
            <DetailsItem>
              <ItemLabel>{get('location_id.label', formFields)}</ItemLabel>
              {/* @TODO: Convert to Location Name */}
              <ItemValue>{get('location_id', guest)}</ItemValue>
            </DetailsItem>
          )}

          {isHomeless ? (
            <DetailsItem>
              <ItemLabel>{get('homeless.label', formFields)}</ItemLabel>
              <RedLabel>Yes</RedLabel>
            </DetailsItem>
          ) : (
            <DetailsItem>
              <ItemLabel>Address</ItemLabel>
              <ItemValue>
                <AccessControl allowOnlyFor={viewAccess(VIEW_ADDRESS)}>
                  {line1}
                </AccessControl>
                <div>{line2}</div>
              </ItemValue>
            </DetailsItem>
          )}

          {(settings.guestShowHomePhone === '1' ||
            settings.guestShowWorkPhone === '1' ||
            settings.guestShowCellPhone === '1') && (
            <DetailsItem>
              <ItemLabel>Phone Number(s)</ItemLabel>
              <ItemValue>
                {!homePhone && !workPhone && !cellPhone && 'None'}
                {settings.guestShowHomePhone === '1' &&
                  homePhone && (
                    <ContactNumber>
                      <small>Home: </small>
                      {homePhone}
                    </ContactNumber>
                  )}
                {settings.guestShowWorkPhone === '1' &&
                  workPhone && (
                    <ContactNumber>
                      <small>Work: </small>
                      {workPhone}
                    </ContactNumber>
                  )}
                {settings.guestShowCellPhone === '1' &&
                  cellPhone && (
                    <ContactNumber>
                      <small>Cell: </small>
                      {cellPhone}
                    </ContactNumber>
                  )}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestRequireEmail !== '0' && (
            <DetailsItem>
              <ItemLabel>{get('email.label', formFields)}</ItemLabel>
              <ItemValue>{get('email', guest)}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalMailingAddress === '1' && (
            <DetailsItem>
              <ItemLabel>{get('mailing_address.label', formFields)}</ItemLabel>
              <ItemValue>
                {mailingAddressLine1}
                {mailingAddressLine1 && <br />}
                {mailingAddressLine2}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestLanguage === '1' && (
            <DetailsItem>
              <ItemLabel>{get('language.label', formFields)}</ItemLabel>
              <ItemValue>{get('language', guest)}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalEducationLast === '1' && (
            <DetailsItem>
              <ItemLabel>{get('educationLast.label', formFields)}</ItemLabel>
              <ItemValue>{guest.educationLast}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalEducation === '1' && (
            <DetailsItem>
              <ItemLabel>{get('education.label', formFields)}</ItemLabel>
              <ItemValue>
                {get(`education.options.${guest.education}`, formFields)}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalGEmployer === '1' && (
            <DetailsItem>
              <ItemLabel>{get('employer.label', formFields)}</ItemLabel>
              <ItemValue>{guest.employer}</ItemValue>
            </DetailsItem>
          )}
          {settings.guestAdditionalGDLNum === '1' && (
            <DetailsItem>
              <ItemLabel>{get('driverLicence.label', formFields)}</ItemLabel>
              <ItemValue>{guest.driverLicence}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalGDLS === '1' && (
            <DetailsItem>
              <ItemLabel>
                {get('driverLicenceState.label', formFields)}
              </ItemLabel>
              <ItemValue>{guest.driverLicenceState}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestMaritalStatus === '1' && (
            <DetailsItem>
              <ItemLabel>{get('marital_status.label', formFields)}</ItemLabel>
              <ItemValue>
                {get(
                  `marital_status.options.${guest.marital_status}`,
                  formFields
                )}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestSpouse === '1' && (
            <DetailsItem>
              <ItemLabel>{get('spouse.label', formFields)}</ItemLabel>
              <ItemValue>{guest.spouse}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalSDLNum === '1' && (
            <DetailsItem>
              <ItemLabel>
                {get('spouseDriverLicence.label', formFields)}
              </ItemLabel>
              <ItemValue>{guest.spouseDriverLicence}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalSDLS === '1' && (
            <DetailsItem>
              <ItemLabel>
                {get('spouseDriverLicenceState.label', formFields)}
              </ItemLabel>
              <ItemValue>{guest.spouseDriverLicenceState}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalSEmployer === '1' && (
            <DetailsItem>
              <ItemLabel>{get('spouseEmployer.label', formFields)}</ItemLabel>
              <ItemValue>{guest.spouseEmployer}</ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalContactHelp === '1' &&
            guest.contactForHelp === 2 && (
              <DetailsItem>
                <ItemLabel>{get('contactForHelp.label', formFields)}</ItemLabel>
                <ItemValue>{get('contactForHelpWhy', guest)}</ItemValue>
              </DetailsItem>
            )}

          {settings.guestAdditionalReasons === '1' && (
            <DetailsItem>
              <ItemLabel>{get('reason.label', formFields)}</ItemLabel>
              <ItemValue>
                {map(
                  val => (
                    <div key={`reason-${val}`}>
                      {get(`reason.options.${val}`, formFields)}
                    </div>
                  ),
                  split(',', guest.reason)
                )}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalIncomeExpense === '1' && (
            <Fragment>
              <hr />
              <ItemLabel>
                <strong>Income and Expenses:</strong>
              </ItemLabel>
              <br />
              <br />
              {/* Static Custom Fields */}
              {times(i => {
                const key = i + 1;
                const label = get(`custom${key}Field.label`, formFields);
                if (settings[`guestCustom${key}Show`] !== '1' || !label)
                  return '';
                return (
                  <DetailsItem key={`customField-${key}`}>
                    <ItemLabel>{label}</ItemLabel>
                    <ItemValue>{get(`custom${key}Field`, guest)}</ItemValue>
                  </DetailsItem>
                );
              }, 5)}
              {/* Income Fields */}
              {times(i => {
                const key = i + 1;
                const label = get(`income${key}.label`, formFields);
                if (settings[`income${key}Status`] !== '1' || !label) return '';
                return (
                  <DetailsItem key={`income${i + 1}`}>
                    <ItemLabel>{label}</ItemLabel>
                    <ItemValue>{get(`income${i + 1}`, guest)}</ItemValue>
                  </DetailsItem>
                );
              }, settings.guestIncomeExpenseColumns)}
              {/* Expenses Fields */}
              {times(i => {
                const key = i + 1;
                const label = get(`expense${key}.label`, formFields);
                if (settings[`expense${key}Status`] !== '1' || !label)
                  return '';
                return (
                  <DetailsItem key={`expense${i + 1}`}>
                    <ItemLabel>{label}</ItemLabel>
                    <ItemValue>{get(`expense${i + 1}`, guest)}</ItemValue>
                  </DetailsItem>
                );
              }, settings.guestIncomeExpenseColumns)}
              {settings.guestAdditionalIncomeExpense === '1' && (
                <Fragment>
                  <DetailsItem>
                    <ItemLabel>
                      {get('incomeTotal.label', formFields)}
                    </ItemLabel>
                    <ItemValue>${get('incomeTotal', guest)}</ItemValue>
                  </DetailsItem>
                  <DetailsItem>
                    <ItemLabel>
                      {get('expenseTotal.label', formFields)}
                    </ItemLabel>
                    <ItemValue>${get('expenseTotal', guest)}</ItemValue>
                  </DetailsItem>
                  <DetailsItem>
                    <ItemLabel>{get('netTotal.label', formFields)}</ItemLabel>
                    <ItemValue>${get('netTotal', guest)}</ItemValue>
                  </DetailsItem>
                </Fragment>
              )}
            </Fragment>
          )}
        </Fragment>
      )}
    </Panel>
  );
};

HouseholdSection.propTypes = {
  guest: GuestType,
  settings: MainSettingsType
};

HouseholdSection.defaultProps = {
  guest: {},
  settings: {}
};

export default HouseholdSection;
